const OrderValidationError = require('@/lib/Validation/OrderValidationError');
const { containsItemRequiringDelivery } = require('@/libraries/DigiTickets/Cart/cartContents');

/**
 * @constructor
 *
 * Perform all necessary validation of a cart required before creating an order. This could either be an order with a
 * payment (including zero cost) made by clicking the 'Pay Now' button or an unpaid kitchen order created by clicking
 * the 'Kitchen' button.
 *
 * This is not necessarily validating the items in the cart, but all requirements for creating an order e.g.:
 * - Is the device online
 * - Is the necessary customer info entered
 *
 * @param $rootScope
 * @param $timeout
 * @param connectivityChecker
 * @param {CurrentDevice} currentDevice
 * @param {EInvoicingService} eInvoicingService
 * @param {FieldValidator} fieldValidator
 * @param {MembershipValidator} membershipValidator
 * @param {OrderAdjustmentConfirmer} orderAdjustmentConfirmer
 * @param {ProductAvailabilityChecker} productAvailabilityChecker
 * @param {UserService} userService
 */
const OrderValidator = function (
    $rootScope,
    $timeout,
    connectivityChecker,
    currentDevice,
    eInvoicingService,
    fieldValidator,
    membershipValidator,
    orderAdjustmentConfirmer,
    productAvailabilityChecker,
    userService
) {
    this.$rootScope = $rootScope;
    this.$timeout = $timeout;
    this.connectivityChecker = connectivityChecker;
    this.currentDevice = currentDevice;
    this.eInvoicingService = eInvoicingService;
    this.fieldValidator = fieldValidator;
    this.membershipValidator = membershipValidator;
    this.orderAdjustmentConfirmer = orderAdjustmentConfirmer;
    this.productAvailabilityChecker = productAvailabilityChecker;
    this.userService = userService;

    // Validation checks that need to be made when clicking the 'Pay Now' button.
    // These will be checked sequentially. If they return a promise it will be awaited.
    this.checkoutValidators = [
        this.checkIfCartEmpty,
        this.checkTradingSession,
        this.checkStockLevels,
        this.checkIfStaffRefRequired,
        this.checkDeliveryAddressSelectedIfGiftVouchers,
        this.checkAccountSelectedIfMemberships,
        this.checkForDuplicateMembershipRefs,
        this.checkMembershipsAreValid,
        this.checkGiftAidIsAnswered,
        this.checkGiftAidCustomerInfoIsEntered,
        this.checkCustomFieldsValid,
        this.checkCustomFieldsHaveBeenPresented,
        this.checkOrderEditRefundDetailsHaveBeenPromptedFor,
        this.checkOrderAdjustmentIsValid,
        this.checkOrderAdjustmentForGreekCompany,
    ];

    // Validation checks that need to be made when clicking the 'Kitchen' button.
    this.sendToKitchenValidators = [
        this.checkIfCartEmpty,
        this.checkTradingSession,
        this.checkStockLevels,
        this.checkCustomFieldsValid,
        this.checkConnectivity,
        this.checkOrderAdjustmentIsValid,
    ];

    // Set the default validation checks.
    this.validators = this.checkoutValidators;
};

OrderValidator.prototype = {

    useCheckoutValidators() {
        this.validators = this.checkoutValidators;
    },

    useSendToKitchenValidators() {
        this.validators = this.sendToKitchenValidators;
    },

    /**
     * Returns a promise which resolves if we can proceed to the payment screen, or rejects with an error
     * code otherwise.
     *
     * @param {CartService} cartService
     *
     * @return {Promise<boolean|string>}
     */
    async validate(cartService) {
        for (let i = 0; i < this.validators.length; i++) {
            // Go through all the specified validators can call them. They should either return true
            // or throw string from OrderValidationError.
            // We can await them all - if it's an async function great. If not it doesn't matter.
            // We purposely want to await in the loop as these should be checked sequentially
            // rather than all running at the same time. The more expensive validations happen last.
            await this.validators[i].bind(this)(cartService);
        }

        return true;
    },

    /**
     * @param {CartService} cartService
     *
     * @return {boolean}
     */
    checkIfCartEmpty(cartService) {
        if (cartService.isEmpty()) {
            throw new OrderValidationError(OrderValidationError.CART_EMPTY);
        }

        return true;
    },

    /**
     * @return {Promise<boolean>}
     */
    async checkConnectivity() {
        if (!this.currentDevice.device.forceOrdersOnline) {
            return true;
        }

        this.$timeout(() => {
            this.$rootScope.setRootProcessingMessage('SELL.PROCESSING_CHECK_CONNECTIVITY');
        });

        return this.connectivityChecker.check()
            .then((isOnline) => {
                if (isOnline) {
                    return true;
                }
                throw new OrderValidationError(OrderValidationError.DEVICE_OFFLINE);
            })
            .catch(() => {
                throw new OrderValidationError(OrderValidationError.DEVICE_OFFLINE);
            })
            .finally(() => {
                this.$timeout(() => {
                    this.$rootScope.setRootProcessingMessage(null);
                });
            });
    },

    /**
     * @param {CartService|Cart} cart
     * @return {boolean}
     */
    checkIfStaffRefRequired(cart) {
        if (
            cart.containsKitchenItems()
            && !cart.staffRef
            && this.userService.currentBranch.kitchenRefRequired
        ) {
            throw new OrderValidationError(OrderValidationError.STAFF_REF_REQUIRED);
        }

        return true;
    },

    /**
     * Returns true of there is an item in the cart that *must* be delivered.
     * The function is duplicated as a method so it can be easily mocked.
     *
     * @param {CartLine[]} cartLines
     *
     * @returns boolean
     */
    containsItemRequiringDelivery(cartLines) {
        return containsItemRequiringDelivery(cartLines);
    },

    /**
     * @param {CartService} cartService
     * @return {boolean}
     */
    checkDeliveryAddressSelectedIfGiftVouchers(cartService) {
        // Validate gift vouchers before continuing
        const requiresDeliveryAddress = this.containsItemRequiringDelivery(cartService.getItems());
        const deliveryAddress = cartService.customer.deliveryAddress;
        if (requiresDeliveryAddress && deliveryAddress.isEmpty()) {
            throw new OrderValidationError(OrderValidationError.DELIVERY_ADDRESS_REQUIRED);
        }

        return true;
    },

    /**
     * @param {CartService} cartService
     * @return {boolean}
     */
    checkAccountSelectedIfMemberships(cartService) {
        // Ensure we still have a customer account selected if there are memberships in the cart
        // (Operator would have been required to have one selected when adding the membership to the cart,
        // but may have de-selected it since then.)
        if (cartService.containsMemberships() && !cartService.customer.hasAccount()) {
            throw new OrderValidationError(OrderValidationError.CUSTOMER_ACCOUNT_REQUIRED);
        }

        return true;
    },

    /**
     * Check the membership validation does not fail for any of the datasets in the cart.
     *
     * @param {CartService} cartService
     * @return {boolean}
     */
    async checkMembershipsAreValid(cartService) {
        // Ensure that any memberships actually have valid member data attached.
        const validationResult = await this.membershipValidator.cartItemDatasetsAreValid(cartService.getItems());
        if (validationResult !== true) {
            let err = new OrderValidationError(OrderValidationError.MISSING_MEMBERSHIP_DATA);
            err.setClientMessages([validationResult]);
            throw err;
        }

        return true;
    },

    /**
     * @param {CartService} cartService
     * @return {boolean}
     */
    checkCustomFieldsValid(cartService) {
        let fieldsValid = this.fieldValidator.validateCartFields(cartService.currentCart);
        if (!fieldsValid) {
            throw (new OrderValidationError(OrderValidationError.FIELD_DATA_INVALID));
        }

        return true;
    },

    /**
     * Has the operator been presented with and had the opportunity to complete each custom field?
     *
     * @param {CartService} cartService
     *
     * @return {Promise<boolean>}
     */
    async checkCustomFieldsHaveBeenPresented(cartService) {
        let haveAllCustomFieldsBeenPresented = await cartService.haveAllCustomFieldsBeenPresented();
        console.debug('Have all current cart fields been presented?', haveAllCustomFieldsBeenPresented);

        // TODO: Discussed in comments of PRO-777. If we add the ability to always display the custom fields
        // upon checkout this should throw the error to trigger the custom fields modal even if
        // haveAllCustomFieldsBeenPresented is null (meaning it was never opened).
        // For now if it was never opened we don't automatically show it.
        if (haveAllCustomFieldsBeenPresented === null) {
            return true;
        }

        if (haveAllCustomFieldsBeenPresented === false) {
            throw (new OrderValidationError(OrderValidationError.ADDITIONAL_CUSTOM_FIELDS_NOT_PRESENTED));
        }

        return true;
    },

    /**
     * @param {CartService} cartService
     * @return {boolean}
     */
    checkForDuplicateMembershipRefs(cartService) {
        // Validate any membership refs in the cart. We can't do a lot, apart from make
        // sure there are no duplicates in the cart.
        let membershipRefErrors = this.membershipValidator.checkForDuplicateRefs(cartService.getMembershipDatasets());
        if (membershipRefErrors.length > 0) {
            throw (new OrderValidationError(OrderValidationError.DUPLICATE_MEMBERSHIP_REFS))
                .setClientMessages(membershipRefErrors);
        }

        return true;
    },

    /**
     * @param {CartService} cartService
     * @return {boolean}
     */
    checkGiftAidIsAnswered(cartService) {
        if (cartService.giftAidEnabled() !== null) {
            // The Gift Aid question has already been answered for the Cart.
            return true;
        }

        if (cartService.getCancellingMemberships().length > 0) {
            // This is a cancellation refund so we don't want to prompt for Gift Aid.
            return true;
        }

        // Check for any Gift Aid-able items in the cart.
        /** @type {CartLine[]} */
        let giftAidItems = cartService.getItems().filter((cartItem) => cartItem.item.giftAid);

        if (giftAidItems.length < 1) {
            return true;
        }

        // Else there are Gift Aid-able items in the cart but the Gift Aid question has not been asked.
        throw (new OrderValidationError(OrderValidationError.GIFT_AID_UNANSWERED))
            .setCartLines(giftAidItems);
    },

    /**
     * @param {CartService} cartService
     * @return {boolean}
     */
    checkGiftAidCustomerInfoIsEntered(cartService) {
        if (!cartService.giftAid) {
            // Gift Aid not enabled so data doesn't matter.
            return true;
        }

        if (!cartService.customer.hasGiftAidData()) {
            throw new OrderValidationError(OrderValidationError.GIFT_AID_CUSTOMER_INFO_REQUIRED);
        }

        return true;
    },

    /**
     * If there are 0 qty lines with no return reasons and no new notes then prompt the operator. We don't want to be
     * too intrusive this is just a nudge - adding a note or a return reason is about the procedures of each client.
     *
     * @param {CartService} cartService
     * @returns {boolean}
     */
    checkOrderEditRefundDetailsHaveBeenPromptedFor(cartService) {
        if (cartService.currentCart.isEditingOrder && !cartService.currentCart.ignoreMissingOrderEditRefundDetails) {
            let linesWithoutReturnReasons = cartService.currentCart.itemList.filter((line) => {
                return line.quantity === 0 && !line.returnReason.reasonID;
            });

            if (
                linesWithoutReturnReasons.length > 0
                && cartService.currentCart.notes === cartService.currentCart.originalOrder.notes
            ) {
                throw new OrderValidationError(OrderValidationError.ORDER_EDIT_REFUND_DETAILS_NOT_ENTERED);
            }
        }
        return true;
    },

    /**
     * @param {CartService} cartService
     *
     * @return {boolean}
     */
    async checkOrderAdjustmentIsValid(cartService) {
        if (!cartService.currentCart.isEditingOrder) {
            return true;
        }

        this.$rootScope.setRootProcessingMessage('SELL.PROCESSING_VALIDATE_ADJUSTMENT');

        try {
            await this.orderAdjustmentConfirmer.validateAdjustment(cartService.currentCart);
            return true;
        } catch (e) {
            throw (new OrderValidationError(OrderValidationError.ADJUSTMENT_VALIDATION_FAILED))
                .setClientMessages([
                    e.message,
                ]);
        } finally {
            this.$rootScope.setRootProcessingMessage(null);
        }
    },

    /**
     * @param {CartService} cartService
     *
     * @returns {boolean}
     */
    checkOrderAdjustmentForGreekCompany(cartService) {
        const cart = cartService.currentCart;

        if (!cart.isEditingOrder) {
            // Not an order edit - don't care.
            return true;
        }

        if (!this.eInvoicingService.inUseForCompany()) {
            // Not a Greek company - don't care.
            return true;
        }

        // Now we have an order edit for a Greek company - the stuff we're interested in.
        // If the total of the cart is positive we need to prevent this. That would result in an additional
        // invoice for the booking, which isn't allowed.
        if (cartService.currentCart.getTotalDue() > 0) {
            // We can't allow this.
            throw new OrderValidationError(OrderValidationError.POSITIVE_VALUE_ADJUSTMENT_FORBIDDEN);
        }

        if (cartService.currentCart.getTotalDue() < 0) {
            if (cart.originalOrder?.nonZeroCreditNoteHasBeenIssued()) {
                // We can't allow this.
                throw new OrderValidationError(OrderValidationError.NEGATIVE_VALUE_ADJUSTMENT_FORBIDDEN);
            }
        }

        return true;
    },

    /**
     * @param {CartService} cartService
     *
     * @return {boolean}
     */
    // eslint-disable-next-line unused-imports/no-unused-vars
    checkTradingSession(cartService) {
        if (!this.currentDevice.getTradingSessionID()) {
            throw new OrderValidationError(OrderValidationError.MISSING_TRADING_SESSION_ID);
        }

        return true;
    },

    /**
     * Check the stock levels of any applicable items. There must either be enough available stock or the operator needs
     * to have authorised the line.
     *
     * Returns null if the device is offline.
     *
     * @param {CartService} cartService
     *
     * @return {?boolean}
     */
    async checkStockLevels(cartService) {
        if (!this.connectivityChecker.isOnline()) {
            return null;
        }

        const cart = cartService.currentCart;

        if (cart.cartProductStockLevels.hasStockLevels()) {
            // Make a new stock level check if it has been longer than the stockCheckInterval duration since last check.
            if (cart.cartProductStockLevels.hasStockCheckIntervalElapsed()) {
                await this.productAvailabilityChecker.check(cart, cartService);
                this.$timeout(() => {
                    // Update UI.
                });
            }

            cart.getItems().forEach((line) => {
                const stockLevel = cart.getStockLevelForLine(line);
                // All necessary stock levels should have already been loaded. So if there is no stock level
                // loaded for this line it probably isn't something that needs checking.
                if (!stockLevel) {
                    return;
                }

                if (stockLevel.disableStockValidation) {
                    return;
                }

                const productQty = cart.getCartProductQuantityForLine(line);
                if (!productQty) {
                    return;
                }

                const qtyRequired = productQty.requiredQuantity;
                const qtyAvailable = stockLevel.availableQty;

                if (qtyRequired > 0 && (qtyAvailable < qtyRequired)) {
                    throw new OrderValidationError(OrderValidationError.STOCK_LEVEL_ERROR);
                }
            });
        }
        return true;
    },
};

module.exports = OrderValidator;
